import API_BASE_URL from '../config/apiBaseUrl';
import { APP_PREFIX } from '../config/appIdentifiers';

const OFFLINE_CONTACT_QUEUE_KEY = `${APP_PREFIX}:offline-contact-messages:v1`;

const tryParseJson = (value) => {
  if (typeof value !== 'string') return null;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const extractErrorMessage = (data) => {
  if (!data) return null;
  if (typeof data === 'string') return data;

  if (typeof data.message === 'string' && data.message.trim()) {
    return data.message;
  }

  if (typeof data.detail === 'string' && data.detail.trim()) {
    const parsed = tryParseJson(data.detail);
    const parsedMessage = extractErrorMessage(parsed);
    return parsedMessage || data.detail;
  }

  if (typeof data['hydra:description'] === 'string' && data['hydra:description'].trim()) {
    return data['hydra:description'];
  }

  if (Array.isArray(data.violations) && data.violations.length > 0) {
    const firstViolation = data.violations[0];
    if (firstViolation?.message) {
      return firstViolation.message;
    }
  }

  if (Array.isArray(data.errors) && data.errors.length > 0) {
    const firstError = data.errors[0];
    if (typeof firstError === 'string') return firstError;
    if (firstError?.message) return firstError.message;
  }

  if (data.errors && typeof data.errors === 'object') {
    const firstError = Object.values(data.errors).find((value) => value);
    if (typeof firstError === 'string') return firstError;
  }

  if (typeof data.error === 'string' && data.error.trim()) {
    return data.error;
  }

  return null;
};

class ContactService {
  getStorage() {
    if (typeof window === 'undefined') {
      return null;
    }

    try {
      return window.localStorage;
    } catch {
      return null;
    }
  }

  isNetworkError(error) {
    if (!error) {
      return false;
    }

    if (error instanceof TypeError) {
      return true;
    }

    const message = String(error.message || '').toLowerCase();
    return message.includes('failed to fetch')
      || message.includes('networkerror')
      || message.includes('network error')
      || message.includes('load failed');
  }

  getQueuedMessages() {
    const storage = this.getStorage();
    if (!storage) {
      return [];
    }

    try {
      const raw = storage.getItem(OFFLINE_CONTACT_QUEUE_KEY);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  setQueuedMessages(queue) {
    const storage = this.getStorage();
    if (!storage) {
      return;
    }

    try {
      storage.setItem(OFFLINE_CONTACT_QUEUE_KEY, JSON.stringify(queue));
    } catch {
      // ignore quota errors
    }
  }

  queueMessage(payload) {
    const queue = this.getQueuedMessages();
    queue.push({
      id: `${Date.now()}-${Math.floor(Math.random() * 1e6).toString(16)}`,
      payload,
      queuedAt: new Date().toISOString(),
    });
    this.setQueuedMessages(queue);
    return queue.length;
  }

  async sendRequest(payload) {
    const response = await fetch(`${API_BASE_URL}/contact`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(payload),
    });

    const data = await response.json().catch(() => ({}));

    if (!response.ok) {
      const messageText = extractErrorMessage(data);
      throw new Error(messageText || 'Erreur lors de l\'envoi du message.');
    }

    return data;
  }

  async send({ name, email, subject, message }) {
    const payload = { name, email, subject, message };

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      this.queueMessage(payload);
      return {
        queued: true,
        message: 'Hors ligne: message enregistré. Il sera envoyé automatiquement dès que la connexion revient.',
      };
    }

    try {
      return await this.sendRequest(payload);
    } catch (error) {
      if (this.isNetworkError(error)) {
        this.queueMessage(payload);
        return {
          queued: true,
          message: 'Connexion indisponible: message enregistré et en attente d’envoi.',
        };
      }

      throw error;
    }
  }

  async syncQueuedMessages() {
    const queue = this.getQueuedMessages();
    if (queue.length === 0) {
      return { sentCount: 0, failedCount: 0, pendingCount: 0 };
    }

    if (typeof navigator !== 'undefined' && !navigator.onLine) {
      return { sentCount: 0, failedCount: 0, pendingCount: queue.length };
    }

    const remaining = [];
    let sentCount = 0;
    let failedCount = 0;

    for (const entry of queue) {
      try {
        await this.sendRequest(entry.payload);
        sentCount += 1;
      } catch (error) {
        failedCount += 1;
        remaining.push(entry);

        if (!this.isNetworkError(error)) {
          console.warn('Echec synchronisation message de contact:', error);
        }
      }
    }

    this.setQueuedMessages(remaining);

    return { sentCount, failedCount, pendingCount: remaining.length };
  }
}

const contactService = new ContactService();
export default contactService;
